/**
 * Confirming a destination account with the client, by phone.
 *
 * The number to dial comes from the brief, which the server fills from the file
 * — never from anything the registration carried. When the channel is not
 * trustworthy (no proven number, or one changed recently) the server returns no
 * number at all, so there is nothing for the screen to offer and nothing for an
 * agent to dial by mistake.
 */
import type { AxiosInstance } from 'axios';
import authService from './auth.service';

export type CallChannel = 'ready' | 'no_proven_phone' | 'phone_recently_changed';
export type CallOutcome = 'confirmed' | 'not_the_client' | 'no_answer';

export interface PendingCheck {
  id: string;
  user_id: string;
  verification_status: 'pending_identity_check' | 'escalated';
  verification_opened_at: string;
  verification_call_attempts: number;
  verification_km_from_usual: string | null;
  country_code: string;
  currency_code: string;
  first_name: string;
  last_name: string;
  email: string;
  /**
   * Each row's own channel, so the queue tells the truth about itself: a task
   * whose client changed their number in the same week is not actionable yet.
   * Without it the row says "waiting for a call", an agent opens it, reads that
   * they cannot call, closes it, and meets it again tomorrow.
   */
  channel: CallChannel;
  callable_from: string | null;
}

export interface CallBrief {
  account_id: string;
  client_id: string;
  client_name: string;
  /** Null whenever the channel is not 'ready'. */
  phone_on_file: string | null;
  channel: CallChannel;
  /**
   * When this becomes callable, for a channel that is not ready yet. Present
   * only for a recently changed number, which clears on a date; a client with
   * no proven number clears when they prove one, so there is no date to give
   * and none is invented.
   */
  callable_from: string | null;
  km_from_usual: number | null;
  /** This client's own usual range, computed when the brief is opened. */
  usual_range_km: number | null;
  attempts_made: number;
  attempts_allowed: number;
  talking_points: string[];
}

class BankAccountChecksService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  async listOpen(): Promise<PendingCheck[]> {
    const res = await this.api.get<PendingCheck[]>('/api/v1/ops/bank-account-checks');
    return res.data;
  }

  async brief(accountId: string): Promise<CallBrief> {
    const res = await this.api.get<CallBrief>(`/api/v1/ops/bank-account-checks/${accountId}`);
    return res.data;
  }

  async recordOutcome(accountId: string, outcome: CallOutcome, notes: string): Promise<void> {
    await this.api.post(`/api/v1/ops/bank-account-checks/${accountId}/outcome`, { outcome, notes });
  }

  /** Manager only. */
  async release(accountId: string, reason: string): Promise<void> {
    await this.api.post(`/api/v1/ops/bank-account-checks/${accountId}/release`, { reason });
  }
}

export default new BankAccountChecksService();
